import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    KeyboardAvoidingView,
    Platform,
    SafeAreaView,
    ScrollView,
    StyleSheet,
    TextInput,
    TouchableOpacity,
    View
} from "react-native";
import { ActivityIndicator, Snackbar, Text } from "react-native-paper";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { SearchEmpty, SearchNoResults } from "@/components/medications/EmptyStates";
import { IcdChipPicker } from "@/components/medications/IcdChipPicker";
import { StrengthQuestion } from "@/components/medications/StrengthQuestion";
import { SuggestionQuestion, SUPPLEMENT_ROUTES } from "@/components/medications/SuggestionQuestion";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { useMedicationFieldSuggestions } from "@/hooks/useMedicationFieldSuggestions";
import {
    createMedication,
    fetchAISuggestions,
    fetchMedications,
    saveMedicationDiagnosisCodes,
    searchMedicationLookup,
    type MedicationPickerOption
} from "@/lib/api";
import { resolveCatalogStrength } from "@/lib/catalog-strength";
import { getSupplementLabel, searchSupplements, type DSLDLabel } from "@/lib/dsld";
import { isPrnFrequency, mddChipSuggestions } from "@/lib/mdd-utils";
import { checkDuplicate } from "@/lib/medication-utils";
import { searchMedications } from "@/lib/rxterms";
import { colors, spacing } from "@/lib/theme";

interface LookupEntry {
    name: string;
    route?: string | null;
    fullName: string;
    strength: string | null;
    isSupplement?: boolean;
    totalCount: number;
    searchTerms: string[];
    dosages: { value: string }[];
    frequencies: { value: string }[];
    adminNotes: { value: string }[];
    diagnoses: { code: string; description: string }[];
}

interface SearchResult {
    name: string;
    subtitle?: string;
    isSupplement: boolean;
    strengthsAndForms: string[];
    dsldId?: string;
    fromLookup?: boolean;
    lookupEntry?: LookupEntry;
}

interface SelectedItem {
    name: string;
    /**
     * null = the nurse has not said which it is yet. Only free-text entries
     * start here; a catalogue, RxTerms or DSLD hit already knows.
     */
    isSupplement: boolean | null;
    strengthsAndForms: string[];
    /** Typed by hand rather than picked, so the type has to be asked. */
    isFreeText?: boolean;
    dsldId?: string;
    fromLookup?: boolean;
    lookupEntry?: LookupEntry;
}

function fromServerLookup(option: MedicationPickerOption): LookupEntry {
    return {
        name: option.name,
        route: option.route,
        fullName: option.name,
        strength: option.strength,
        isSupplement: option.isSupplement,
        totalCount: option.rows,
        searchTerms: [option.name, option.label, option.strengthForm, option.route].filter(
            Boolean
        ) as string[],
        dosages: option.doses.map((value) => ({ value })),
        frequencies: option.frequencies.map((value) => ({ value })),
        adminNotes: option.administrationNotes.map((value) => ({ value })),
        diagnoses: []
    };
}

function mergeLookupEntry(
    entry: LookupEntry,
    suggestions: {
        doses?: string[];
        frequencies?: string[];
        administrationNotes?: string[];
        icdCodes?: { code: string; description: string }[];
    }
): LookupEntry {
    return {
        ...entry,
        dosages: [...entry.dosages, ...(suggestions.doses ?? []).map((value) => ({ value }))],
        frequencies: [
            ...entry.frequencies,
            ...(suggestions.frequencies ?? []).map((value) => ({ value }))
        ],
        adminNotes: [
            ...entry.adminNotes,
            ...(suggestions.administrationNotes ?? []).map((value) => ({ value }))
        ],
        diagnoses: [...entry.diagnoses, ...(suggestions.icdCodes ?? [])]
    };
}

export default function SearchScreen() {
    const { id: visitId } = useLocalSearchParams<{ id: string }>();
    const router = useRouter();

    // Prevent double-navigation from snackbar timer + manual back press
    const hasNavigatedRef = useRef(false);
    const navTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    useEffect(() => {
        return () => {
            if (navTimerRef.current) {
                clearTimeout(navTimerRef.current);
            }
        };
    }, []);

    const { dialogState, showDialog, dismissDialog } = useConfirmDialog();

    const [query, setQuery] = useState("");
    const [results, setResults] = useState<SearchResult[]>([]);
    const [searching, setSearching] = useState(false);
    const [selected, setSelected] = useState<SelectedItem | null>(null);
    const [selectedDose, setSelectedDose] = useState<string | null>(null);
    const [customDose, setCustomDose] = useState("");
    const [showCustomDose, setShowCustomDose] = useState(false);
    const showCustomDoseRef = useRef(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // DSLD enriched supplement state
    const [dsldLabel, setDsldLabel] = useState<DSLDLabel | null>(null);
    const [loadingLabel, setLoadingLabel] = useState(false);
    const [suppOrderedDose, setSuppOrderedDose] = useState("");
    const [suppFrequency, setSuppFrequency] = useState("");
    const [suppAdminNotes, setSuppAdminNotes] = useState("");
    const [suppRoute, setSuppRoute] = useState("");
    const [savingSupplement, setSavingSupplement] = useState(false);
    const [snackbar, setSnackbar] = useState<string | null>(null);

    // Inline review state (for lookup meds — single-screen flow)
    const [lookupFreq, setLookupFreq] = useState<string | null>(null);
    const [lookupCustomFreq, setLookupCustomFreq] = useState("");
    const [showLookupCustomFreq, setShowLookupCustomFreq] = useState(false);
    const [lookupNotes, setLookupNotes] = useState<Set<string>>(new Set());
    // MDD — offered (optional) when the chosen frequency is PRN
    const [lookupMddChip, setLookupMddChip] = useState<string | null>(null);
    const [lookupCustomMdd, setLookupCustomMdd] = useState("");
    const [showLookupCustomMdd, setShowLookupCustomMdd] = useState(false);
    const [lookupIcds, setLookupIcds] = useState<Set<string>>(new Set());
    const [lookupAddedIcds, setLookupAddedIcds] = useState<{ code: string; description: string }[]>(
        []
    );
    const [loadingLookupDetails, setLoadingLookupDetails] = useState(false);
    const [savingLookup, setSavingLookup] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [lookupStrength, setLookupStrength] = useState("");
    const [lookupStrengthNotApplicable, setLookupStrengthNotApplicable] = useState(false);
    const catalogStrength = resolveCatalogStrength({
        catalogStrength: selected?.lookupEntry?.strength,
        enteredStrength: lookupStrength,
        notApplicable: lookupStrengthNotApplicable,
        isSupplement: selected?.isSupplement === true
    });

    const supplementStrength = resolveCatalogStrength({
        enteredStrength: customDose,
        notApplicable: lookupStrengthNotApplicable,
        isSupplement: true
    });

    const supplementSuggestions = useMedicationFieldSuggestions(
        visitId,
        selected?.name ?? "",
        ["doses", "frequency", "administrationNotes"],
        Boolean(dsldLabel)
    );

    const setCustomDoseMode = useCallback((enabled: boolean) => {
        showCustomDoseRef.current = enabled;
        setShowCustomDose(enabled);
    }, []);

    const handleSearch = useCallback(
        (text: string) => {
            setQuery(text);
            setSelected(null);
            setSuppOrderedDose("");
            setSuppFrequency("");
            setSuppAdminNotes("");
            setSuppRoute("");
            setLookupStrength("");
            setLookupStrengthNotApplicable(false);
            setSelectedDose(null);
            setCustomDose("");
            setCustomDoseMode(false);
            setDsldLabel(null);
            setLoadingLookupDetails(false);

            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }

            if (text.length < 2) {
                setResults([]);
                return;
            }

            // Medication search is server-side so the RN picker uses the same
            // manual-search DB as the API/Lambda path. Supplements keep their
            // separate DSLD search/enrichment path below.
            setResults([]);

            // Then fetch external APIs in background
            debounceRef.current = setTimeout(async () => {
                setSearching(true);
                try {
                    const [serverLookup, meds, supps] = await Promise.all([
                        visitId
                            ? searchMedicationLookup(visitId, text, 12).catch(() => ({
                                  options: [] as MedicationPickerOption[]
                              }))
                            : Promise.resolve({ options: [] as MedicationPickerOption[] }),
                        searchMedications(text),
                        searchSupplements(text)
                    ]);
                    const lookupResults: SearchResult[] = serverLookup.options.map((option) => {
                        const entry = fromServerLookup(option);
                        return {
                            name: entry.fullName,
                            subtitle: `${entry.totalCount.toLocaleString()} local ${entry.isSupplement ? "supplement " : ""}rows · dose/frequency ready`,
                            isSupplement: entry.isSupplement ?? false,
                            strengthsAndForms: entry.dosages.map((d) => d.value),
                            fromLookup: true,
                            lookupEntry: entry
                        };
                    });

                    // Dedup: remove RxTerms results that match a lookup entry
                    const lookupNames = new Set(
                        lookupResults.flatMap((r) =>
                            (r.lookupEntry?.searchTerms ?? []).map((t) => t.toLowerCase())
                        )
                    );

                    const medResults: SearchResult[] = meds
                        .filter((m) => !lookupNames.has(m.name.toLowerCase()))
                        .map((m) => ({
                            name: m.name,
                            subtitle: `${m.strengthsAndForms.length} dose option${m.strengthsAndForms.length === 1 ? "" : "s"} available`,
                            isSupplement: false,
                            strengthsAndForms: m.strengthsAndForms
                        }));
                    const suppResults: SearchResult[] = supps.map((s) => ({
                        name: s.name,
                        subtitle: s.brandName ? `${s.brandName} · Supplement` : "Supplement",
                        isSupplement: true,
                        strengthsAndForms: [],
                        dsldId: s.dsldId
                    }));

                    // Our DB results first, then external
                    setResults([...lookupResults, ...medResults, ...suppResults]);
                } catch {
                    setResults([]);
                } finally {
                    setSearching(false);
                }
            }, 300);
        },
        [setCustomDoseMode, visitId]
    );

    const handleSelectItem = async (item: SearchResult) => {
        setSuppOrderedDose("");
        setSuppFrequency("");
        setSuppAdminNotes("");
        setSuppRoute(item.lookupEntry?.route ?? "");
        setLookupStrength("");
        setLookupStrengthNotApplicable(false);
        setSelected({
            name: item.name,
            isSupplement: item.isSupplement,
            strengthsAndForms: item.strengthsAndForms,
            dsldId: item.dsldId,
            fromLookup: item.fromLookup,
            lookupEntry: item.lookupEntry
        });
        setSelectedDose(null);
        setCustomDose("");
        setCustomDoseMode(false);
        setQuery(item.name);
        setResults([]);

        // Reset per-med state so a new selection never inherits the prior med's
        // frequency/diagnosis chips (prefill below only sets fields the table has).
        setLookupFreq(null);
        setLookupCustomFreq("");
        setShowLookupCustomFreq(false);
        setLookupMddChip(null);
        setLookupCustomMdd("");
        setShowLookupCustomMdd(false);
        setLookupNotes(new Set());
        setLookupIcds(new Set());
        setLookupAddedIcds([]);
        setLoadingLookupDetails(false);
        setShowErrors(false);

        // Pre-select top options from lookup data
        if (item.fromLookup && item.lookupEntry) {
            const le = item.lookupEntry;
            if (le.dosages.length > 0) {
                setSelectedDose(le.dosages[0].value);
            }
            if (le.frequencies.length > 0) {
                setLookupFreq(le.frequencies[0].value);
            }
            if (le.adminNotes.length > 0) {
                setLookupNotes(new Set([le.adminNotes[0].value]));
            }
            if (le.diagnoses.length > 0) {
                setLookupAddedIcds(
                    le.diagnoses.map((d) => ({ code: d.code, description: d.description }))
                );
                setLookupIcds(new Set([le.diagnoses[0].code]));
            }

            if (visitId) {
                const missingFields: (
                    "frequency" | "administrationNotes" | "icdCodes" | "doses"
                )[] = [];
                if (le.dosages.length === 0) {
                    missingFields.push("doses");
                }
                if (le.frequencies.length === 0) {
                    missingFields.push("frequency");
                }
                if (le.adminNotes.length === 0) {
                    missingFields.push("administrationNotes");
                }
                if (le.diagnoses.length === 0) {
                    missingFields.push("icdCodes");
                }

                if (missingFields.length > 0) {
                    setLoadingLookupDetails(true);
                    fetchAISuggestions(
                        visitId,
                        le.fullName,
                        undefined,
                        missingFields,
                        true,
                        le.strength ?? undefined
                    )
                        .then((suggestions) => {
                            const merged = mergeLookupEntry(le, suggestions);
                            setSelected((prev) =>
                                prev?.lookupEntry === le
                                    ? {
                                          ...prev,
                                          lookupEntry: merged,
                                          strengthsAndForms: merged.dosages.map((d) => d.value)
                                      }
                                    : prev
                            );
                            if (merged.dosages.length > 0) {
                                setSelectedDose((current) => {
                                    if (current || showCustomDoseRef.current) {
                                        return current;
                                    }
                                    return merged.dosages[0].value;
                                });
                            }
                            // Functional updates: this callback resolves seconds after the
                            // tap that started it, so reading lookupFreq/lookupNotes/
                            // lookupIcds from the closure would see their values at tap
                            // time (always empty) and clobber whatever the nurse picked
                            // while the lookup was in flight.
                            if (merged.frequencies.length > 0) {
                                setLookupFreq((prev) => prev ?? merged.frequencies[0].value);
                            }
                            if (merged.adminNotes.length > 0) {
                                setLookupNotes((prev) =>
                                    prev.size === 0 ? new Set([merged.adminNotes[0].value]) : prev
                                );
                            }
                            if (merged.diagnoses.length > 0) {
                                setLookupAddedIcds((prev) =>
                                    prev.length === 0
                                        ? merged.diagnoses.map((d) => ({
                                              code: d.code,
                                              description: d.description
                                          }))
                                        : prev
                                );
                                setLookupIcds((prev) =>
                                    prev.size === 0 ? new Set([merged.diagnoses[0].code]) : prev
                                );
                            }
                        })
                        .catch(() => {
                            // Keep DB-backed suggestions; the RN can add missing fields manually.
                        })
                        .finally(() => setLoadingLookupDetails(false));
                }
            }
        }

        // If DSLD supplement — fetch full label
        if (item.isSupplement && item.dsldId) {
            setLoadingLabel(true);
            setDsldLabel(null);
            const label = await getSupplementLabel(item.dsldId);
            if (label) {
                setDsldLabel(label);
                setCustomDose(label.dosage ?? "");
                setSuppFrequency(label.frequency ?? "");
                setSuppAdminNotes(label.adminNotes ?? "");
                setSuppRoute(label.route ?? "");
            }
            setLoadingLabel(false);
        } else {
            setDsldLabel(null);
        }
    };

    const handleFreeText = () => {
        setLookupStrengthNotApplicable(false);
        setCustomDose("");
        setSelectedDose(null);
        setSuppRoute("");
        setSelected({
            name: query,
            // Undecided. This used to be `true`: every free-text entry was filed
            // as a supplement without asking, which is how 105 distinct
            // prescription drugs — Ozempic, Xarelto, levothyroxine, oxycodone —
            // reached patient charts carrying a "(Supplement)" chip, and how
            // they skipped the dose, frequency and diagnosis requirements that
            // only apply to medications. She answers it now.
            isSupplement: null,
            isFreeText: true,
            strengthsAndForms: []
        });
        setCustomDoseMode(true);
        setDsldLabel(null);
        setResults([]);
    };

    // Can proceed to step 2 (for medications and free-text supplements).
    // An unanswered type blocks it outright — defaulting the answer is the bug
    // this screen used to have, and a default in the other direction would be
    // just as wrong.
    const canProceed =
        selected &&
        !dsldLabel &&
        selected.isSupplement !== null &&
        (!selected.fromLookup || catalogStrength.ready) &&
        (selected.isSupplement
            ? supplementStrength.ready
            : Boolean(selectedDose?.trim() || customDose.trim() || lookupStrengthNotApplicable));

    const handleNext = () => {
        if (!selected || !visitId || !canProceed) {
            return;
        }
        const dose = (selectedDose ?? customDose).trim() || undefined;
        const strength = selected.fromLookup
            ? catalogStrength.strength
            : selected.isSupplement
              ? supplementStrength.strength
              : dose;
        const orderedDose = selected.fromLookup ? dose : undefined;
        router.push({
            pathname: `/visit/${visitId}/medications/review`,
            params: {
                medicationName: selected.name,
                dose: dose ?? "",
                strength: strength ?? "",
                strengthNotApplicable: lookupStrengthNotApplicable ? "true" : "false",
                route: suppRoute,
                orderedDose: orderedDose ?? "",
                isSupplement: selected.isSupplement === true ? "true" : "false",
                fromLookup: selected.fromLookup ? "true" : "false",
                lookupData: selected.lookupEntry ? JSON.stringify(selected.lookupEntry) : ""
            }
        });
    };

    // Direct save for DSLD supplements (skip step 2)
    const handleSaveSupplement = async () => {
        if (!selected || !visitId) {
            return;
        }
        if (
            !supplementStrength.ready ||
            !suppOrderedDose.trim() ||
            !suppFrequency.trim() ||
            !suppRoute.trim()
        ) {
            setShowErrors(true);
            return;
        }
        setSavingSupplement(true);
        try {
            await createMedication(visitId, {
                medicationName: selected.name,
                strength: supplementStrength.strength,
                strengthNotApplicable: lookupStrengthNotApplicable,
                orderedDose: suppOrderedDose.trim(),
                frequency: suppFrequency.trim(),
                administrationNotes: suppAdminNotes || undefined,
                isSupplement: true,
                route: suppRoute.trim() || undefined
            });
            setSnackbar(`${selected.name} saved`);
            navTimerRef.current = setTimeout(() => {
                if (!hasNavigatedRef.current) {
                    hasNavigatedRef.current = true;
                    router.back();
                }
            }, 800);
        } catch {
            showDialog("Error", "Failed to save supplement. Please try again.");
        } finally {
            setSavingSupplement(false);
        }
    };

    // Direct save for lookup meds (single-screen flow)
    const handleSaveLookupMed = async () => {
        if (!selected || !visitId) {
            return;
        }
        const freq = (showLookupCustomFreq ? lookupCustomFreq : lookupFreq)?.trim();
        const dose = (selectedDose ?? customDose).trim() || undefined;
        // Same rule as the review screen: a medication needs the amount taken at
        // one time. The catalogue pre-selects one, so this only bites on an entry
        // that offers no dose options at all.
        if (
            !catalogStrength.ready ||
            !dose ||
            !freq ||
            (selected.isSupplement ? !suppRoute.trim() : lookupIcds.size === 0)
        ) {
            setShowErrors(true);
            return;
        }

        setSavingLookup(true);
        try {
            const existingMeds = await fetchMedications(visitId);
            const dup = checkDuplicate(existingMeds, selected.name, selected.isSupplement === true);
            if (dup) {
                setSavingLookup(false);
                showDialog(
                    selected.isSupplement
                        ? "Supplement Already on Profile"
                        : "Medication Already on Profile",
                    `${selected.name} is already on the patient's profile.`,
                    [{ text: "OK", style: "cancel" }]
                );
                return;
            }

            const notes = [...lookupNotes].join(", ");
            const codes = lookupAddedIcds.filter((c) => lookupIcds.has(c.code));

            const med = await createMedication(visitId, {
                medicationName: selected.name,
                strength: catalogStrength.strength,
                strengthNotApplicable: catalogStrength.needsEntry && lookupStrengthNotApplicable,
                orderedDose: dose,
                frequency: freq,
                administrationNotes: notes || undefined,
                maxDailyDose: askLookupMdd ? (effectiveLookupMdd ?? undefined) : undefined,
                isSupplement: selected.isSupplement === true,
                route: selected.isSupplement
                    ? suppRoute.trim()
                    : (selected.lookupEntry?.route ?? undefined)
            });

            if (codes.length > 0 && med?.id) {
                await saveMedicationDiagnosisCodes(
                    visitId,
                    String(med.id),
                    codes.map((c) => ({
                        icdCode: c.code,
                        description: c.description
                    }))
                );
            }

            setSnackbar(`${selected.name} saved`);
            navTimerRef.current = setTimeout(() => {
                if (!hasNavigatedRef.current) {
                    hasNavigatedRef.current = true;
                    router.back();
                }
            }, 800);
        } catch {
            showDialog("Error", "Failed to save medication. Please try again.");
        } finally {
            setSavingLookup(false);
        }
    };

    // Effective frequency for the lookup-med flow: the typed custom value when
    // "Other" is active, otherwise the selected chip.
    const effectiveLookupFreq = showLookupCustomFreq ? lookupCustomFreq : lookupFreq;
    // MDD (optional on manual add): derived chip from the chosen dose × schedule,
    // "Other" for free text. Same field the draft card and edit screen carry.
    const askLookupMdd = isPrnFrequency(effectiveLookupFreq) && !selected?.isSupplement;
    const lookupMddSuggestions = askLookupMdd
        ? mddChipSuggestions(selectedDose ?? (customDose || null), effectiveLookupFreq)
        : [];
    const effectiveLookupMdd = showLookupCustomMdd ? lookupCustomMdd.trim() || null : lookupMddChip;

    // Never preselected — the ceiling is always the nurse's explicit tap. And a
    // tapped chip must stay visible: when a frequency/dose change re-derives the
    // suggestions and the chosen value is no longer among them, drop the
    // selection rather than silently saving a ceiling the card no longer shows.
    const lookupMddValues = lookupMddSuggestions.map((sg) => sg.value).join("|");
    useEffect(() => {
        if (
            lookupMddChip &&
            !showLookupCustomMdd &&
            !lookupMddValues.split("|").includes(lookupMddChip)
        ) {
            setLookupMddChip(null);
        }
    }, [lookupMddChip, showLookupCustomMdd, lookupMddValues]);

    return (
        <SafeAreaView style={styles.safe}>
            {/* Header */}
            <View style={styles.wizardHeader}>
                <Text style={styles.stepLabel}>
                    {dsldLabel
                        ? "SUPPLEMENT DETAILS"
                        : selected?.fromLookup
                          ? "ADD MEDICATION"
                          : "STEP 1 OF 2"}
                </Text>
                <Text style={styles.wizardTitle}>Search Medication or Supplement</Text>
                <View style={styles.progressBar}>
                    <View
                        style={[
                            styles.progressFill,
                            { width: dsldLabel || selected?.fromLookup ? "100%" : "50%" }
                        ]}
                    />
                </View>
            </View>

            <KeyboardAvoidingView
                style={styles.flexFill}
                behavior={Platform.OS === "ios" ? "padding" : "height"}
            >
                <ScrollView
                    style={styles.content}
                    keyboardShouldPersistTaps="handled"
                    automaticallyAdjustKeyboardInsets={Platform.OS === "ios"}
                >
                    {/* Search input */}
                    <Text style={styles.instructionText}>
                        Start typing the medication name to see available options.
                    </Text>
                    <View style={styles.searchWrap}>
                        <Text style={styles.searchIcon}>{"\uD83D\uDD0D"}</Text>
                        <TextInput
                            style={styles.searchInput}
                            placeholder="Type medication or supplement name..."
                            placeholderTextColor="#B0AECE"
                            value={query}
                            onChangeText={handleSearch}
                            autoFocus
                            autoCorrect={false}
                            autoCapitalize="none"
                            spellCheck={false}
                        />
                    </View>

                    {/* Search results */}
                    {searching && (
                        <View style={styles.loadingRow}>
                            <ActivityIndicator size="small" color="#7C3AED" />
                            <Text style={styles.loadingText}>Searching...</Text>
                        </View>
                    )}

                    {!searching && results.length > 0 && !selected && (
                        <View style={styles.resultsList}>
                            {results.map((item, i) => (
                                <TouchableOpacity
                                    key={i}
                                    style={styles.resultItem}
                                    onPress={() => handleSelectItem(item)}
                                >
                                    <Text style={styles.resultName}>{item.name}</Text>
                                    {item.subtitle && (
                                        <Text
                                            style={[
                                                styles.resultForm,
                                                item.isSupplement && styles.resultSupplement
                                            ]}
                                        >
                                            {item.subtitle}
                                        </Text>
                                    )}
                                </TouchableOpacity>
                            ))}
                            {/* Free text fallback */}
                            <TouchableOpacity style={styles.freeTextItem} onPress={handleFreeText}>
                                <Text style={styles.freeTextLabel}>
                                    Can't find it? Tap here to type freely
                                </Text>
                                <Text style={styles.freeTextHint}>
                                    You'll be asked if it's a medication or a supplement
                                </Text>
                            </TouchableOpacity>
                        </View>
                    )}

                    {!searching && query.length >= 2 && results.length === 0 && !selected && (
                        <SearchNoResults query={query} onFreeText={handleFreeText} />
                    )}

                    {!searching && query.length < 2 && !selected && <SearchEmpty />}

                    {/* DSLD label loading */}
                    {loadingLabel && (
                        <View style={styles.loadingRow}>
                            <ActivityIndicator size="small" color="#7C3AED" />
                            <Text style={styles.loadingText}>Loading supplement details...</Text>
                        </View>
                    )}

                    {/* ── DSLD Enriched Supplement Panel ── */}
                    {selected && dsldLabel && !loadingLabel && (
                        <View style={styles.doseSection}>
                            <View style={styles.suppHeader}>
                                <Text style={styles.suppName}>{selected.name}</Text>
                                <View style={styles.suppBadge}>
                                    <Text style={styles.suppBadgeText}>Supplement</Text>
                                </View>
                            </View>

                            <StrengthQuestion
                                visitId={visitId}
                                productName={selected.name}
                                value={customDose}
                                onChange={setCustomDose}
                                notApplicable={lookupStrengthNotApplicable}
                                onNotApplicableChange={setLookupStrengthNotApplicable}
                                showError={showErrors}
                            />
                            <SuggestionQuestion
                                label="Amount taken"
                                required
                                value={suppOrderedDose}
                                onChange={setSuppOrderedDose}
                                options={supplementSuggestions.suggestions?.doses ?? []}
                                loading={supplementSuggestions.loading}
                                showError={showErrors}
                                placeholder="For example, 2 tablets"
                                errorMessage="Select the amount taken at one time"
                            />
                            <SuggestionQuestion
                                label="Frequency"
                                required
                                value={suppFrequency}
                                onChange={setSuppFrequency}
                                options={supplementSuggestions.suggestions?.frequencies ?? []}
                                loading={supplementSuggestions.loading}
                                showError={showErrors}
                                placeholder="Enter frequency"
                            />
                            <SuggestionQuestion
                                label="Administration notes"
                                value={suppAdminNotes}
                                onChange={setSuppAdminNotes}
                                options={
                                    supplementSuggestions.suggestions?.administrationNotes ?? []
                                }
                                loading={supplementSuggestions.loading}
                                placeholder="Enter administration notes"
                            />
                            <SuggestionQuestion
                                label="Route"
                                required
                                value={suppRoute}
                                onChange={setSuppRoute}
                                options={SUPPLEMENT_ROUTES}
                                showError={showErrors}
                                placeholder="Enter route"
                            />
                        </View>
                    )}

                    {/* ── LOOKUP MED: Full inline review (single-screen) ── */}
                    {selected && selected.fromLookup && selected.lookupEntry && (
                        <View style={styles.doseSection}>
                            {catalogStrength.needsEntry && (
                                <StrengthQuestion
                                    visitId={visitId}
                                    productName={selected.name}
                                    value={lookupStrength}
                                    onChange={setLookupStrength}
                                    notApplicable={lookupStrengthNotApplicable}
                                    onNotApplicableChange={setLookupStrengthNotApplicable}
                                    showError={showErrors}
                                />
                            )}
                            {selected.isSupplement && (
                                <SuggestionQuestion
                                    label="Route"
                                    required
                                    value={suppRoute}
                                    onChange={setSuppRoute}
                                    options={SUPPLEMENT_ROUTES}
                                    showError={showErrors}
                                    placeholder="Enter route"
                                />
                            )}
                            {/* Dose */}
                            <Text
                                style={[
                                    styles.doseLabel,
                                    showErrors &&
                                        !(selectedDose ?? (customDose || undefined)) && {
                                            color: "#DC2626"
                                        }
                                ]}
                            >
                                Select Dose *
                            </Text>
                            <View style={styles.chipGroup}>
                                {selected.lookupEntry.dosages.map((d) => (
                                    <TouchableOpacity
                                        key={d.value}
                                        style={[
                                            styles.chip,
                                            selectedDose === d.value && styles.chipSelected
                                        ]}
                                        onPress={() => {
                                            setSelectedDose(d.value);
                                            setCustomDose("");
                                            setCustomDoseMode(false);
                                        }}
                                    >
                                        <Text
                                            style={[
                                                styles.chipText,
                                                selectedDose === d.value && styles.chipTextSelected
                                            ]}
                                        >
                                            {d.value}
                                        </Text>
                                    </TouchableOpacity>
                                ))}
                                <TouchableOpacity
                                    style={[
                                        styles.chip,
                                        styles.chipOther,
                                        showCustomDose && styles.chipSelected
                                    ]}
                                    onPress={() => {
                                        setSelectedDose(null);
                                        setCustomDoseMode(true);
                                    }}
                                >
                                    <Text
                                        style={[
                                            styles.chipText,
                                            showCustomDose && styles.chipTextSelected
                                        ]}
                                    >
                                        Other
                                    </Text>
                                </TouchableOpacity>
                            </View>
                            {showCustomDose && (
                                <TextInput
                                    style={[styles.doseInput, { marginTop: 8 }]}
                                    placeholder="Amount per dose, e.g. 1 tablet"
                                    placeholderTextColor="#B0AECE"
                                    value={customDose}
                                    onChangeText={setCustomDose}
                                    autoFocus
                                    autoCorrect={false}
                                    spellCheck={false}
                                />
                            )}
                            {showErrors && !(selectedDose ?? (customDose || undefined)) && (
                                <Text style={styles.fieldError}>
                                    Please select the amount taken at one time
                                </Text>
                            )}
                            {loadingLookupDetails && (
                                <View style={styles.loadingRow}>
                                    <ActivityIndicator size="small" color="#7C3AED" />
                                    <Text style={styles.loadingText}>
                                        Looking up missing suggestions...
                                    </Text>
                                </View>
                            )}

                            {/* Frequency */}
                            <Text
                                style={[
                                    styles.doseLabel,
                                    { marginTop: 20 },
                                    showErrors && !effectiveLookupFreq && { color: "#DC2626" }
                                ]}
                            >
                                Frequency *
                            </Text>
                            <View style={styles.chipGroup}>
                                {selected.lookupEntry.frequencies.map((f) => (
                                    <TouchableOpacity
                                        key={f.value}
                                        style={[
                                            styles.chip,
                                            lookupFreq === f.value &&
                                                !showLookupCustomFreq &&
                                                styles.chipSelected
                                        ]}
                                        onPress={() => {
                                            setLookupFreq(f.value);
                                            setShowLookupCustomFreq(false);
                                        }}
                                    >
                                        <Text
                                            style={[
                                                styles.chipText,
                                                lookupFreq === f.value &&
                                                    !showLookupCustomFreq &&
                                                    styles.chipTextSelected
                                            ]}
                                        >
                                            {f.value}
                                        </Text>
                                    </TouchableOpacity>
                                ))}
                                <TouchableOpacity
                                    style={[
                                        styles.chip,
                                        { borderStyle: "dashed" as const, borderColor: "#B0AECE" },
                                        showLookupCustomFreq && styles.chipSelected
                                    ]}
                                    onPress={() => {
                                        setShowLookupCustomFreq(true);
                                        setLookupFreq(null);
                                    }}
                                >
                                    <Text
                                        style={[
                                            styles.chipText,
                                            showLookupCustomFreq && styles.chipTextSelected
                                        ]}
                                    >
                                        Other
                                    </Text>
                                </TouchableOpacity>
                            </View>
                            {showLookupCustomFreq && (
                                <TextInput
                                    style={[styles.doseInput, { marginTop: 8 }]}
                                    placeholder="Enter custom frequency..."
                                    placeholderTextColor="#B0AECE"
                                    value={lookupCustomFreq}
                                    onChangeText={setLookupCustomFreq}
                                    autoFocus
                                    autoCorrect={false}
                                    spellCheck={false}
                                />
                            )}
                            {showErrors && !effectiveLookupFreq && (
                                <Text style={styles.fieldError}>Please select a frequency</Text>
                            )}

                            {/* Max daily dose — optional, offered for PRN frequencies */}
                            {askLookupMdd && (
                                <>
                                    <Text style={[styles.doseLabel, { marginTop: 20 }]}>
                                        Max Daily Dose{" "}
                                        <Text style={styles.optionalTag}>(optional)</Text>
                                    </Text>
                                    <View style={styles.chipGroup}>
                                        {lookupMddSuggestions.map((sg) => (
                                            <TouchableOpacity
                                                key={sg.value}
                                                style={[
                                                    styles.chip,
                                                    lookupMddChip === sg.value &&
                                                        !showLookupCustomMdd &&
                                                        styles.chipSelected
                                                ]}
                                                onPress={() => {
                                                    setLookupMddChip(
                                                        !showLookupCustomMdd &&
                                                            lookupMddChip === sg.value
                                                            ? null
                                                            : sg.value
                                                    );
                                                    setShowLookupCustomMdd(false);
                                                }}
                                            >
                                                <Text
                                                    style={[
                                                        styles.chipText,
                                                        lookupMddChip === sg.value &&
                                                            !showLookupCustomMdd &&
                                                            styles.chipTextSelected
                                                    ]}
                                                >
                                                    {sg.value} ({sg.explain})
                                                </Text>
                                            </TouchableOpacity>
                                        ))}
                                        <TouchableOpacity
                                            style={[
                                                styles.chip,
                                                {
                                                    borderStyle: "dashed" as const,
                                                    borderColor: "#B0AECE"
                                                },
                                                showLookupCustomMdd && styles.chipSelected
                                            ]}
                                            onPress={() => {
                                                setShowLookupCustomMdd(true);
                                                setLookupMddChip(null);
                                            }}
                                        >
                                            <Text
                                                style={[
                                                    styles.chipText,
                                                    showLookupCustomMdd && styles.chipTextSelected
                                                ]}
                                            >
                                                Other
                                            </Text>
                                        </TouchableOpacity>
                                    </View>
                                    {showLookupCustomMdd && (
                                        <TextInput
                                            style={[styles.doseInput, { marginTop: 8 }]}
                                            placeholder="e.g., 8 tablets"
                                            placeholderTextColor="#B0AECE"
                                            value={lookupCustomMdd}
                                            onChangeText={setLookupCustomMdd}
                                            autoFocus
                                            autoCorrect={false}
                                            spellCheck={false}
                                        />
                                    )}
                                    <Text style={styles.mddHintText}>
                                        The most the patient may take in 24 hours.
                                    </Text>
                                </>
                            )}

                            {/* Admin Notes */}
                            {selected.lookupEntry.adminNotes.length > 0 && (
                                <>
                                    <Text style={[styles.doseLabel, { marginTop: 20 }]}>
                                        Administration Notes
                                    </Text>
                                    <View style={styles.chipGroup}>
                                        {selected.lookupEntry.adminNotes.map((n) => (
                                            <TouchableOpacity
                                                key={n.value}
                                                style={[
                                                    styles.chip,
                                                    lookupNotes.has(n.value) && styles.chipSelected
                                                ]}
                                                onPress={() => {
                                                    setLookupNotes((prev) => {
                                                        const next = new Set(prev);
                                                        if (next.has(n.value)) {
                                                            next.delete(n.value);
                                                        } else {
                                                            next.add(n.value);
                                                        }
                                                        return next;
                                                    });
                                                }}
                                            >
                                                <Text
                                                    style={[
                                                        styles.chipText,
                                                        lookupNotes.has(n.value) &&
                                                            styles.chipTextSelected
                                                    ]}
                                                >
                                                    {n.value}
                                                </Text>
                                            </TouchableOpacity>
                                        ))}
                                    </View>
                                </>
                            )}

                            {/* Diagnosis Codes — always shown; required for every med.
                                When the lookup table has no suggestions the RN adds a code via
                                the picker's "Other" → ICD search. */}
                            <Text
                                style={[
                                    styles.doseLabel,
                                    { marginTop: 20 },
                                    showErrors &&
                                        !selected.isSupplement &&
                                        lookupIcds.size === 0 && { color: "#DC2626" }
                                ]}
                            >
                                Diagnosis Codes {selected.isSupplement ? "(optional)" : "*"}
                            </Text>
                            <IcdChipPicker
                                suggestedCodes={lookupAddedIcds}
                                selectedCodes={lookupIcds}
                                onToggle={(code) => {
                                    setLookupIcds((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(code)) {
                                            next.delete(code);
                                        } else {
                                            next.add(code);
                                        }
                                        return next;
                                    });
                                }}
                                onAdd={(code) => {
                                    setLookupAddedIcds((prev) => [...prev, code]);
                                    setLookupIcds((prev) => new Set([...prev, code.code]));
                                }}
                            />
                            {showErrors && !selected.isSupplement && lookupIcds.size === 0 && (
                                <Text style={styles.fieldError}>
                                    Select at least one diagnosis code
                                </Text>
                            )}

                            <Text
                                style={{
                                    fontSize: 11,
                                    color: "#B0AECE",
                                    marginTop: 16,
                                    textAlign: "center"
                                }}
                            >
                                Based on {selected.lookupEntry.totalCount.toLocaleString()} nurse
                                entries
                            </Text>
                        </View>
                    )}

                    {/* Medication or supplement — asked, never assumed. Stays on screen
                        after she answers so the choice is visible and changeable. */}
                    {selected && selected.isFreeText && !dsldLabel && !loadingLabel && (
                        <View style={styles.doseSection}>
                            <Text style={styles.doseLabel}>
                                Is this a medication or a supplement?
                            </Text>
                            <Text style={styles.doseSubtitle}>{selected.name}</Text>
                            <View style={styles.chipGroup}>
                                {[
                                    { label: "Medication", value: false },
                                    { label: "Supplement", value: true }
                                ].map((option) => {
                                    const chosen = selected.isSupplement === option.value;
                                    return (
                                        <TouchableOpacity
                                            key={option.label}
                                            style={[styles.chip, chosen && styles.chipSelected]}
                                            onPress={() => {
                                                setSelected({
                                                    ...selected,
                                                    isSupplement: option.value
                                                });
                                                setCustomDoseMode(true);
                                            }}
                                        >
                                            <Text
                                                style={[
                                                    styles.chipText,
                                                    chosen && styles.chipTextSelected
                                                ]}
                                            >
                                                {option.label}
                                            </Text>
                                        </TouchableOpacity>
                                    );
                                })}
                            </View>
                            <Text style={styles.freeTextHint}>
                                A supplement is a vitamin, mineral, herb or probiotic. Anything
                                prescribed is a medication.
                            </Text>
                        </View>
                    )}

                    {/* Dose selection — shown after selecting a medication (RxNorm, NOT from lookup) */}
                    {selected && selected.isSupplement === false && !selected.fromLookup && (
                        <View style={styles.doseSection}>
                            {/* Required for a medication — Next is disabled without it, so
                                say so rather than leaving a dead button unexplained. Supplements also need an explicit strength decision. */}
                            {selected.strengthsAndForms.length > 0 && (
                                <>
                                    <Text style={styles.doseLabel}>Select Strength *</Text>
                                    <Text style={styles.doseSubtitle}>{selected.name}</Text>
                                </>
                            )}
                            {selected.strengthsAndForms.length > 0 ? (
                                <View style={styles.chipGroup}>
                                    {selected.strengthsAndForms.map((dose) => (
                                        <TouchableOpacity
                                            key={dose}
                                            style={[
                                                styles.chip,
                                                selectedDose === dose && styles.chipSelected
                                            ]}
                                            onPress={() => {
                                                setSelectedDose(dose);
                                                setCustomDose("");
                                                setCustomDoseMode(false);
                                            }}
                                        >
                                            <Text
                                                style={[
                                                    styles.chipText,
                                                    selectedDose === dose && styles.chipTextSelected
                                                ]}
                                            >
                                                {dose}
                                            </Text>
                                        </TouchableOpacity>
                                    ))}
                                    <TouchableOpacity
                                        style={[
                                            styles.chip,
                                            styles.chipOther,
                                            showCustomDose && styles.chipSelected
                                        ]}
                                        onPress={() => {
                                            setSelectedDose(null);
                                            setCustomDoseMode(true);
                                        }}
                                    >
                                        <Text
                                            style={[
                                                styles.chipText,
                                                showCustomDose && styles.chipTextSelected
                                            ]}
                                        >
                                            Other
                                        </Text>
                                    </TouchableOpacity>
                                </View>
                            ) : (
                                <StrengthQuestion
                                    visitId={visitId}
                                    productName={selected.name}
                                    value={customDose}
                                    onChange={setCustomDose}
                                    notApplicable={lookupStrengthNotApplicable}
                                    onNotApplicableChange={setLookupStrengthNotApplicable}
                                />
                            )}
                            {selected.strengthsAndForms.length > 0 && showCustomDose && (
                                <TextInput
                                    style={[styles.doseInput, { marginTop: 8 }]}
                                    placeholder="Enter custom dose..."
                                    placeholderTextColor="#B0AECE"
                                    value={customDose}
                                    onChangeText={setCustomDose}
                                    autoFocus
                                    autoCorrect={false}
                                    spellCheck={false}
                                />
                            )}
                        </View>
                    )}

                    {selected &&
                        selected.isSupplement === true &&
                        !selected.fromLookup &&
                        !dsldLabel &&
                        !loadingLabel && (
                            <View style={styles.doseSection}>
                                <StrengthQuestion
                                    visitId={visitId}
                                    productName={selected.name}
                                    value={customDose}
                                    onChange={setCustomDose}
                                    notApplicable={lookupStrengthNotApplicable}
                                    onNotApplicableChange={setLookupStrengthNotApplicable}
                                />
                            </View>
                        )}
                </ScrollView>
            </KeyboardAvoidingView>

            {/* Sticky bottom */}
            <View style={styles.stickyBottom}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => router.back()}>
                    <Text style={styles.cancelText}>Cancel</Text>
                </TouchableOpacity>

                {/* DSLD supplement → direct save */}
                {dsldLabel ? (
                    <TouchableOpacity
                        style={[styles.saveBtn, savingSupplement && styles.nextBtnDisabled]}
                        onPress={handleSaveSupplement}
                        disabled={savingSupplement}
                    >
                        {savingSupplement ? (
                            <ActivityIndicator size="small" color="#FFFFFF" />
                        ) : (
                            <Text style={styles.nextText}>Save Supplement</Text>
                        )}
                    </TouchableOpacity>
                ) : selected?.fromLookup ? (
                    /* Lookup med → direct save (green button) */
                    <TouchableOpacity
                        style={[styles.saveBtn, savingLookup && styles.nextBtnDisabled]}
                        onPress={handleSaveLookupMed}
                        disabled={savingLookup}
                    >
                        {savingLookup ? (
                            <ActivityIndicator size="small" color="#FFFFFF" />
                        ) : (
                            <Text style={styles.nextText}>
                                Save {selected.isSupplement ? "Supplement" : "Medication"}
                            </Text>
                        )}
                    </TouchableOpacity>
                ) : (
                    /* Regular flow → Next to review screen */
                    <TouchableOpacity
                        style={[styles.nextBtn, !canProceed && styles.nextBtnDisabled]}
                        onPress={handleNext}
                        disabled={!canProceed}
                    >
                        <Text style={styles.nextText}>Next</Text>
                    </TouchableOpacity>
                )}
            </View>

            <Snackbar
                visible={snackbar !== null}
                onDismiss={() => setSnackbar(null)}
                duration={1500}
                style={{ backgroundColor: "#1F1547" }}
            >
                {snackbar ? `\u2713 ${snackbar}` : ""}
            </Snackbar>
            <ConfirmDialog {...dialogState} onDismiss={dismissDialog} />
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    safe: { flex: 1, backgroundColor: "#FAFAF9" },
    flexFill: { flex: 1 },
    wizardHeader: {
        paddingHorizontal: 20,
        paddingTop: 20,
        paddingBottom: 16,
        backgroundColor: "#FFFFFF"
    },
    stepLabel: {
        fontSize: 11,
        fontWeight: "700",
        letterSpacing: 1,
        color: "#A78BFA",
        textTransform: "uppercase",
        marginBottom: 4
    },
    wizardTitle: {
        fontSize: 20,
        fontWeight: "800",
        color: "#1F1547",
        marginBottom: 10
    },
    progressBar: {
        height: 4,
        backgroundColor: "#EDE9FE",
        borderRadius: 4,
        overflow: "hidden"
    },
    progressFill: {
        height: "100%",
        backgroundColor: "#A78BFA",
        borderRadius: 4
    },
    content: { flex: 1, padding: 20 },
    instructionText: {
        fontSize: 13,
        color: "#6B6B8D",
        marginBottom: 12
    },
    searchWrap: {
        position: "relative",
        marginBottom: 12
    },
    searchIcon: {
        position: "absolute",
        left: 14,
        top: 14,
        fontSize: 16,
        zIndex: 1
    },
    searchInput: {
        borderWidth: 2,
        borderColor: "#EDE9FE",
        borderRadius: 12,
        padding: 14,
        paddingLeft: 42,
        fontSize: 15,
        color: "#1F1547",
        backgroundColor: "#FFFFFF"
    },
    loadingRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        paddingVertical: 12
    },
    loadingText: { fontSize: 13, color: "#6B6B8D" },
    resultsList: {
        borderWidth: 1,
        borderColor: "#EDE9FE",
        borderRadius: 12,
        overflow: "hidden"
    },
    resultItem: {
        padding: 14,
        borderBottomWidth: 1,
        borderBottomColor: "#FAFAF9"
    },
    resultName: {
        fontSize: 14,
        fontWeight: "600",
        color: "#1F1547"
    },
    resultForm: {
        fontSize: 12,
        color: "#6B6B8D",
        marginTop: 2
    },
    resultSupplement: {
        color: "#7C3AED",
        fontWeight: "600"
    },
    freeTextItem: {
        padding: 14,
        backgroundColor: "#F5F3FF",
        borderTopWidth: 1,
        borderTopColor: "#EDE9FE"
    },
    freeTextLabel: {
        fontSize: 13,
        fontWeight: "600",
        color: "#7C3AED"
    },
    freeTextHint: {
        fontSize: 11,
        color: "#B0AECE",
        marginTop: 2
    },
    doseSection: {
        marginTop: 20,
        paddingTop: 20,
        borderTopWidth: 1,
        borderTopColor: "#EDE9FE"
    },
    doseLabel: {
        fontSize: 15,
        fontWeight: "700",
        color: "#1F1547",
        marginBottom: 4
    },
    fieldError: {
        fontSize: 11,
        color: "#EF4444",
        fontWeight: "600",
        marginTop: 4
    },
    optionalTag: { fontSize: 10, color: "#B0AECE" },
    mddHintText: { fontSize: 11, color: "#B45309", marginTop: 8 },
    doseSubtitle: {
        fontSize: 12,
        color: "#6B6B8D",
        marginBottom: 12
    },
    chipGroup: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8
    },
    chip: {
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 20,
        borderWidth: 2,
        borderColor: "#EDE9FE",
        backgroundColor: "#FFFFFF"
    },
    chipSelected: {
        borderColor: "#A78BFA",
        backgroundColor: "#A78BFA"
    },
    chipOther: {
        borderStyle: "dashed",
        borderColor: "#B0AECE"
    },
    chipText: {
        fontSize: 13,
        fontWeight: "600",
        color: "#6B6B8D"
    },
    chipTextSelected: {
        color: "#FFFFFF"
    },
    doseInput: {
        borderWidth: 2,
        borderColor: "#EDE9FE",
        borderRadius: 12,
        padding: 14,
        fontSize: 15,
        color: "#1F1547",
        backgroundColor: "#FFFFFF",
        marginBottom: 12
    },
    skipDose: {
        fontSize: 13,
        color: "#7C3AED",
        fontWeight: "600",
        marginTop: 2
    },
    // DSLD supplement panel
    suppHeader: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginBottom: 16,
        flexWrap: "wrap"
    },
    suppName: {
        fontSize: 16,
        fontWeight: "700",
        color: "#1F1547",
        flex: 1
    },
    suppBadge: {
        backgroundColor: "#EDE9FE",
        paddingHorizontal: 10,
        paddingVertical: 3,
        borderRadius: 8
    },
    suppBadgeText: {
        fontSize: 11,
        fontWeight: "700",
        color: "#7C3AED"
    },
    fieldLabel: {
        fontSize: 13,
        fontWeight: "600",
        color: "#6B6B8D",
        marginBottom: 4
    },
    // Bottom bar
    stickyBottom: {
        flexDirection: "row",
        gap: 10,
        paddingHorizontal: 20,
        paddingTop: 12,
        paddingBottom: 28,
        backgroundColor: "#FFFFFF",
        borderTopWidth: 1,
        borderTopColor: "#EDE9FE"
    },
    cancelBtn: {
        flex: 1,
        alignItems: "center",
        paddingVertical: 14,
        borderRadius: 20,
        borderWidth: 2,
        borderColor: "#EDE9FE"
    },
    cancelText: { fontSize: 15, fontWeight: "700", color: "#6B6B8D" },
    nextBtn: {
        flex: 1,
        alignItems: "center",
        paddingVertical: 14,
        borderRadius: 20,
        backgroundColor: "#7C3AED"
    },
    saveBtn: {
        flex: 1,
        alignItems: "center",
        paddingVertical: 14,
        borderRadius: 20,
        backgroundColor: "#059669"
    },
    nextBtnDisabled: { opacity: 0.4 },
    nextText: { fontSize: 15, fontWeight: "700", color: "#FFFFFF" }
});
